import { Injectable, signal, NgZone, isDevMode } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  deferredPrompt: any;
  showInstallPrompt = signal<boolean>(false);
  isStandalone = false;
  
  // Debug flag to force show the install button
  debugShowInstall = isDevMode();
  
  // Method to check if PWA requirements are met
  private async checkPWARequirements(): Promise<boolean> {
    const requirements = {
      manifest: false,
      serviceWorker: false,
      https: false,
      isAndroid: false,
      browserSupport: false
    };
    
    // Check HTTPS (required for PWA)
    requirements.https = location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
    
    // Check if Android
    requirements.isAndroid = /android/i.test(navigator.userAgent);
    
    // Check browser support
    const userAgent = navigator.userAgent.toLowerCase();
    requirements.browserSupport = /chrome|samsung|firefox|edge/.test(userAgent);
    
    // Check manifest
    try {
      const manifestLinks = document.querySelectorAll('link[rel="manifest"]');
      if (manifestLinks.length > 0) {
        requirements.manifest = true;
        // Try to fetch and validate manifest
        const manifestHref = (manifestLinks[0] as HTMLLinkElement).href;
        try {
          const response = await fetch(manifestHref);
          const manifest = await response.json();
          console.log('Manifest loaded successfully:', manifest);
        } catch (error) {
          console.warn('Could not fetch manifest:', error);
          requirements.manifest = false;
        }
      }
    } catch (error) {
      console.warn('Could not check manifest:', error);
    }
    
    // Check service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        requirements.serviceWorker = !!registration;
        if (registration) {
          console.log('Service Worker registered:', registration);
        } else {
          console.log('Service Worker not registered yet');
        }
      } catch (error) {
        console.warn('Could not check service worker:', error);
      }
    }
    
    const allRequirementsMet = Object.values(requirements).every(req => req);
    console.log('PWA Requirements:', requirements, 'All met:', allRequirementsMet);
    
    // Additional Android-specific checks
    if (requirements.isAndroid) {
      console.log('Android device detected');
      if (!requirements.browserSupport) {
        console.warn('Browser may not support PWA installation');
      }
      if (!requirements.https && location.hostname !== 'localhost') {
        console.error('HTTPS required for PWA on Android (except localhost)');
      }
    }
    
    return allRequirementsMet;
  }

  constructor(private zone: NgZone) {
    console.log('PwaInstallService initializing...');
    this.checkIfStandalone();
    this.initializeInstallPrompt();
    
    // Check PWA requirements immediately and also after delay
    this.checkPWARequirements();
    setTimeout(() => this.checkPWARequirements(), 2000);
    
    // Force show install prompt in development for testing
    if (isDevMode()) {
      console.log('Development mode: Will show install prompt after 5 seconds for testing');
      setTimeout(() => {
        if (!this.isStandalone) {
          this.zone.run(() => {
            this.showInstallPrompt.set(true);
            console.log('Development: Force showing install prompt');
          });
        }
      }, 5000);
    }
  }

  // Check if the app is already installed
  private checkIfStandalone(): void {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         (window.navigator as any).standalone || 
                         document.referrer.includes('android-app://');
                         
    this.isStandalone = isStandalone;
    console.log('Is running as standalone PWA:', isStandalone);
    
    // If in debug mode and not standalone, we'll show the install prompt anyway
    if (this.debugShowInstall && !isStandalone) {
      console.log('Debug mode: Showing install prompt after delay');
      setTimeout(() => {
        this.zone.run(() => {
          this.showInstallPrompt.set(true);
        });
      }, 3000);
    }
  }

  initializeInstallPrompt() {
    console.log('Initializing PWA install prompt handler');
    console.log('User Agent:', navigator.userAgent);
    console.log('Is Android:', /android/i.test(navigator.userAgent));
    console.log('Current URL:', window.location.href);
    console.log('Protocol:', window.location.protocol);
    
    // Check if service worker is supported and registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          console.log('Service Worker is ready - PWA conditions met', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    } else {
      console.warn('Service Worker not supported - PWA may not work properly');
    }
    
    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('🎉 beforeinstallprompt event fired!', e);
      
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      
      // Stash the event so it can be triggered later
      this.deferredPrompt = e;
      
      // Update UI to show install button (in NgZone to ensure Angular detects the change)
      this.zone.run(() => {
        this.showInstallPrompt.set(true);
        console.log('✅ Install prompt UI should now be visible');
      });
    });

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', (evt) => {
      console.log('🎉 PWA was installed successfully', evt);
      
      // Hide the install prompt
      this.zone.run(() => {
        this.showInstallPrompt.set(false);
        this.isStandalone = true;
      });
      
      // Clear the deferredPrompt
      this.deferredPrompt = null;
    });
    
    // Additional debugging for Android
    if (/android/i.test(navigator.userAgent)) {
      console.log('Android device detected - setting up additional monitoring');
      
      // Check if already installed
      setTimeout(() => {
        const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
        console.log('Is app already installed?', isInstalled);
        
        if (!isInstalled && !this.deferredPrompt) {
          console.log('No install prompt received after 3 seconds on Android');
          console.log('This might be normal - not all conditions may be met');
        }
      }, 3000);
    }
  }

  promptInstall() {
    if (this.deferredPrompt) {
      console.log('Showing the native install prompt');
      // Show the native prompt
      this.deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      this.deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }
        // We no longer need the prompt. Clear it
        this.deferredPrompt = null;
        this.zone.run(() => {
          this.showInstallPrompt.set(false);
        });
      });
    } else if (this.debugShowInstall) {
      console.log('Using manual installation instructions (debug mode)');
      
      // Detect user agent for specific instructions
      const userAgent = navigator.userAgent.toLowerCase();
      
      // For iOS devices
      if (/ipad|iphone|ipod/.test(userAgent)) {
        alert('To install this app on iOS: tap the Share button and then "Add to Home Screen".');
      } 
      // For Android Chrome/Samsung Internet
      else if (/android/.test(userAgent)) {
        if (/chrome/.test(userAgent)) {
          alert('To install this app on Android Chrome: tap the menu (⋮) and select "Add to Home screen" or "Install app".');
        } else if (/samsungbrowser/.test(userAgent)) {
          alert('To install this app on Samsung Internet: tap the menu and select "Add page to" → "Home screen".');
        } else {
          alert('To install this app on Android: open the browser menu and look for "Add to Home Screen" or "Install App".');
        }
      }
      // For other browsers where we don't have the prompt
      else {
        alert('To install this app: open the browser menu and select "Install App" or "Add to Home Screen".');
      }
      
      // Hide the prompt after showing instructions
      this.zone.run(() => {
        this.showInstallPrompt.set(false);
      });
    }
  }

  // Method to get PWA status for debugging
  async getPWAStatus(): Promise<any> {
    const status = {
      isStandalone: this.isStandalone,
      hasInstallPrompt: !!this.deferredPrompt,
      showInstallPrompt: this.showInstallPrompt(),
      userAgent: navigator.userAgent,
      displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
      requirements: await this.checkPWARequirements(),
      currentUrl: window.location.href,
      isDevMode: isDevMode(),
      manifestPresent: document.querySelectorAll('link[rel="manifest"]').length > 0,
      serviceWorkerSupported: 'serviceWorker' in navigator
    };
    
    console.log('PWA Status:', status);
    return status;
  }

  // Force show install prompt for testing (dev only)
  forceShowInstallPrompt(): void {
    if (isDevMode()) {
      // console.log('🧪 Force showing install prompt for testing');
      this.zone.run(() => {
        this.showInstallPrompt.set(true);
      });
    } else {
      console.warn('forceShowInstallPrompt only works in development mode');
    }
  }
}
